import { VehicleDataAge } from '@agilox/common';
import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { STALE_HOURS_BY_LEVEL } from '../constants/stale-hours-by-level.const';
import { VEHICLE_DATA_PILL_TRANSLATION_PREFIX } from '../constants/vehicle-data-pill-translation-prefix.const';
import { VehicleDataActuality } from '../interfaces';
import { vehicleDataLabel } from '../utils/vehicle-data-actuality.util';
import { VehicleDataType } from '../enums/vehicle-data-type.enum';

const TOOLTIP_PREFIX = `${VEHICLE_DATA_PILL_TRANSLATION_PREFIX}.tooltip`;
const SINGLE_VEHICLE_TOOLTIP_PREFIX = `${TOOLTIP_PREFIX}.single_vehicle`;
const SCOPE_ALL_KEY = 'scope_all';
const SCOPE_SOME_KEY = 'scope_some';
const UP_TO_DATE_KEY = 'up_to_date';

const OUTDATED_ISSUE_KEY_BY_DATA_TYPE: Record<VehicleDataType, string> = {
	[VehicleDataType.BBOX]: 'bbox_outdated',
	[VehicleDataType.ORDER]: 'order_outdated',
};

const MISSING_ISSUE_KEY_BY_DATA_TYPE: Record<VehicleDataType, string> = {
	[VehicleDataType.BBOX]: 'bbox_missing',
	[VehicleDataType.ORDER]: 'order_missing',
};

const BOTH_OUTDATED_ISSUE_KEY = 'both_outdated';
const ANY_MISSING_ISSUE_KEY = 'any_missing';

@Pipe({
	name: 'vehicleDataActualityTooltip',
})
export class VehicleDataActualityTooltipPipe implements PipeTransform {
	private translate = inject(TranslateService);

	transform(actuality: VehicleDataActuality | undefined, singleVehicle = false): string {
		const prefix = singleVehicle ? SINGLE_VEHICLE_TOOLTIP_PREFIX : TOOLTIP_PREFIX;

		if (!actuality || actuality.level === VehicleDataAge.UP_TO_DATE) {
			return this.translate.instant(`${prefix}.${UP_TO_DATE_KEY}`);
		}

		const hours = { hours: STALE_HOURS_BY_LEVEL[actuality.level] };

		return this.translate.instant(
			`${prefix}.${this.issueKey(actuality)}`,
			singleVehicle ? hours : { ...hours, scope: this.scope(actuality) }
		);
	}

	private issueKey(actuality: VehicleDataActuality): string {
		const isMissing = actuality.level === VehicleDataAge.MISSING;

		if (actuality.affectedDataTypes.length !== 1) {
			return isMissing ? ANY_MISSING_ISSUE_KEY : BOTH_OUTDATED_ISSUE_KEY;
		}

		const dataType = actuality.affectedDataTypes[0];
		return isMissing
			? MISSING_ISSUE_KEY_BY_DATA_TYPE[dataType]
			: OUTDATED_ISSUE_KEY_BY_DATA_TYPE[dataType];
	}

	private scope(actuality: VehicleDataActuality): string {
		const affected = actuality.affectedVehicles.length;

		if (affected === 1) {
			return vehicleDataLabel(actuality.affectedVehicles[0]);
		}
		if (affected === actuality.totalVehicles) {
			return this.translate.instant(`${TOOLTIP_PREFIX}.${SCOPE_ALL_KEY}`);
		}

		return this.translate.instant(`${TOOLTIP_PREFIX}.${SCOPE_SOME_KEY}`, {
			affected,
			total: actuality.totalVehicles,
		});
	}
}
